import * as React from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';

import { Order } from '@models';
import { IApplicationState } from '@store';
import { HOCLoader, Message } from '@components';

import OrdersList from './orders/OrdersList';
import OrderHeader from './OrderHeader';
import { RequestStatus } from '../../core/store/types/request';
import LocaleLinks from '../locale/LocaleLinks';
import { OrderListSort, sortOrdersByType, useSortedOrders } from './common';
import './Orders.scss';

export interface IOrdersStateProps {
  orders: Order[];
}

const QUERY_DIRECTION = 'direction';
const QUERY_SORT = 'sort';

const Orders: React.FC<IOrdersStateProps> = ({ orders }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const history = useHistory();

  const [isDesc, setIsDesc] = React.useState(true);
  const [sortType, setSortType] = React.useState<OrderListSort>(
    OrderListSort.Uploaded
  );
  const timezone = useSelector(
    (state: IApplicationState) => state.projectPicker.timeZone
  );
  const requestStatus: RequestStatus = useSelector(
    (state: IApplicationState) => state.orders.status
  );

  const sortedOrders = sortOrdersByType(
    useSortedOrders(orders, timezone),
    sortType,
    isDesc,
    t
  );

  React.useEffect(() => {
    const direction = query.get(QUERY_DIRECTION);
    const sort = query.get(QUERY_SORT);

    if (direction) {
      setIsDesc(direction === 'desc');
    }

    if (sort) {
      setSortType(+sort as OrderListSort);
    }
  }, [orders]);

  const onSort = (newSortType: OrderListSort) => {
    const ascending = sortType === newSortType && isDesc;

    setIsDesc(!ascending);
    setSortType(newSortType);

    query.set(QUERY_DIRECTION, ascending ? 'asc' : 'desc');
    query.set(QUERY_SORT, newSortType.toString());

    location.search = query.toString();
    history.push(location);
  };

  const emptyOrdersMessageCmp = (
    <>
      <hr />
      <Message type='empty' title={t('orders_emptyListTitle')}>
        <div className='fcolor--text-label font-size-14'>
          <LocaleLinks
            localeKey='orders_emptyListDescription'
            links={[{ name: 'emptyOrders', route: '/uploads' }]}
          />
        </div>
      </Message>
    </>
  );

  return (
    <div className='orders-common orders page mt-2'>
      <Helmet>
        <title>{t('orders_title')}</title>
      </Helmet>
      <OrderHeader sortedOrders={sortedOrders} />
      <HOCLoader
        status={requestStatus}
        isEmpty={orders.length === 0}
        customEmpty={emptyOrdersMessageCmp}
      >
        <OrdersList
          sort={onSort}
          sortedOrders={sortedOrders}
          sortType={sortType}
          sortDescending={isDesc}
        />
      </HOCLoader>
    </div>
  );
};

export default Orders;
